import { ApolloError } from 'apollo-client';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { Logger } from './Logger';
import {
  ITelemetryScenarioTrackerData,
  ScenarioColumns,
  TelemetryScenarioErrorStatus,
  TelemetryScenarioName,
  TelemetryScenarioStatus,
  TelemetryScenarioStep
} from './TelemetryScenarioTracker.types';

/**
 * A class designed to be used by our React Components to track a scenario.
 *
 * You can call "start()" for a new telemetry scenario to be started. (i.e. the Component starts loading)
 * You can then call to "addEvent" to log a new event on that scenario.
 * You can then call to "finish()" to say that the telemetry scenario is done. (i.e. the Component has loaded)
 */
export class TelemetryScenarioTracker {
  private static trackerMap: Map<
    string,
    ITelemetryScenarioTrackerData
  > = new Map();

  public static start(
    scenarioName:
      | TelemetryScenarioName
      | string
      | Array<TelemetryScenarioName | string>,
    scenarioType: string,
    message?: string
  ): ITelemetryScenarioTrackerData | void {
    const startTime = moment();
    const id = uuid();

    scenarioName = TelemetryScenarioTracker._getScenarioName(scenarioName);
    const trackerKey = TelemetryScenarioTracker._getTrackerKey(
      scenarioName,
      scenarioType
    );

    if (TelemetryScenarioTracker.trackerMap.has(trackerKey)) {
      Logger.warn(
        'Trying to call start in TelemetryScenarioTracker for an already-started scenario' +
          scenarioName +
          ' ' +
          scenarioType +
          '. Resetting the start time. Calling addEvent instead.'
      );

      let telemetryEvent = TelemetryScenarioTracker.trackerMap.get(trackerKey)!;
      telemetryEvent.startTime = startTime;

      return TelemetryScenarioTracker.addEvent(
        scenarioName,
        scenarioType,
        `${scenarioName} started [duplicated call to 'start']${
          message ? `. Message: ${message}` : ''
        }`,
        TelemetryScenarioStep.Start
      );
    } else {
      const timeStamp = moment();
      const telemetryEvent: ITelemetryScenarioTrackerData = {
        id,
        [ScenarioColumns.ScenarioName]: scenarioName,
        [ScenarioColumns.ScenarioStep]: TelemetryScenarioStep.Start,
        [ScenarioColumns.ScenarioStatus]: TelemetryScenarioStatus.Init,
        scenarioType,
        startTime,
        lastUpdatedTime: startTime,
        eventNumber: 1,
        stepDelta: timeStamp.diff(startTime).toString(),
        message: `${scenarioName} started${
          message ? `. Message: ${message}` : ''
        }`
      };

      TelemetryScenarioTracker.trackerMap.set(trackerKey, telemetryEvent);
      Logger.logScenarioTrackerEvent(telemetryEvent);
      return telemetryEvent;
    }
  }

  public static addEvent(
    scenarioName:
      | TelemetryScenarioName
      | string
      | Array<TelemetryScenarioName | string>,
    scenarioType: string,
    message: string,
    step?: TelemetryScenarioStep
  ): ITelemetryScenarioTrackerData | void {
    const updatedTime = moment();

    scenarioName = TelemetryScenarioTracker._getScenarioName(scenarioName);
    const trackerKey = TelemetryScenarioTracker._getTrackerKey(
      scenarioName,
      scenarioType
    );

    if (!TelemetryScenarioTracker.trackerMap.has(trackerKey)) {
      Logger.warn(
        `Trying to call addEvent in TelemetryScenarioTracker without previously calling start for scenario ${scenarioName} ${scenarioType}.
        Calling start instead.`
      );

      TelemetryScenarioTracker.start(
        scenarioName,
        scenarioType,
        `[missing call to 'start']`
      );
    }

    let telemetryEvent = TelemetryScenarioTracker.trackerMap.get(trackerKey)!;
    // We check above if the event doesn't exsists then it's reinitialzed with a valid eventNumber.
    telemetryEvent.eventNumber! += 1;
    telemetryEvent.lastUpdatedTime = updatedTime;
    TelemetryScenarioTracker.trackerMap.set(trackerKey, telemetryEvent);

    Logger.logScenarioTrackerEvent({
      [ScenarioColumns.ScenarioName]: scenarioName,
      scenarioType,
      id: telemetryEvent.id,
      message: message,
      eventNumber: telemetryEvent.eventNumber,
      [ScenarioColumns.ScenarioStep]: step,
      stepDelta: telemetryEvent.lastUpdatedTime
        .diff(telemetryEvent.startTime)
        .toString()
    });
    return telemetryEvent;
  }

  public static finish(
    scenarioName:
      | TelemetryScenarioName
      | string
      | Array<TelemetryScenarioName | string>,
    scenarioType: string,
    error?: Error,
    message?: string
  ): ITelemetryScenarioTrackerData | void {
    const finishTime = moment();

    scenarioName = TelemetryScenarioTracker._getScenarioName(scenarioName);
    const trackerKey = TelemetryScenarioTracker._getTrackerKey(
      scenarioName,
      scenarioType
    );

    if (!TelemetryScenarioTracker.trackerMap.has(trackerKey)) {
      Logger.warn(
        `Trying to call finish in TelemetryScenarioTracker without previously calling start for scenario ${scenarioName} ${scenarioType}.`
      );
    } else {
      let telemetryEvent = TelemetryScenarioTracker.trackerMap.get(trackerKey)!;
      const stepDelta = finishTime.diff(telemetryEvent.lastUpdatedTime);

      telemetryEvent.eventNumber
        ? (telemetryEvent.eventNumber += 1)
        : (telemetryEvent.eventNumber = 1);
      telemetryEvent.lastUpdatedTime = finishTime;
      telemetryEvent.finishTime = finishTime;

      const elapsedTime = moment.duration(
        finishTime.diff(telemetryEvent.startTime)
      );

      if (error) {
        telemetryEvent.error = error;
        telemetryEvent.errorCode = (error as ApolloError)?.networkError
          ? TelemetryScenarioErrorStatus.NetworkError
          : TelemetryScenarioErrorStatus.Error;
      }

      Logger.logScenarioTrackerEvent({
        ...telemetryEvent,
        [ScenarioColumns.ScenarioName]: scenarioName,
        scenarioType: scenarioType,
        id: telemetryEvent.id,
        [ScenarioColumns.ScenarioStep]: TelemetryScenarioStep.Finish,
        [ScenarioColumns.ScenarioStatus]: !error
          ? TelemetryScenarioStatus.Success
          : TelemetryScenarioStatus.Failure,
        message: `${scenarioName} finished${
          message ? `. Message: ${message}` : ''
        }`,
        stepDelta: stepDelta.toString(),
        elapsed: elapsedTime.asMilliseconds(),
        eventNumber: telemetryEvent.eventNumber
      });

      TelemetryScenarioTracker.trackerMap.delete(trackerKey);
      return telemetryEvent;
    }
  }

  public static withScenarioTelemetry<T>(
    scenarioName: string | Array<string>,
    scenarioType: string,
    fn: () => T
  ): T {
    TelemetryScenarioTracker.start(scenarioName, scenarioType);

    try {
      let result = fn();

      TelemetryScenarioTracker.finish(scenarioName, scenarioType);
      return result;
    } catch (e) {
      TelemetryScenarioTracker.finish(scenarioName, scenarioType, e);
      throw e;
    }
  }

  private static _getScenarioName(
    scenarioName: string | Array<string>
  ): string {
    if (Array.isArray(scenarioName)) {
      return scenarioName.join('/');
    } else {
      return scenarioName;
    }
  }

  private static _getTrackerKey(
    scenarioName: string,
    scenarioType: string
  ): string {
    return `${scenarioName}_${scenarioType}`;
  }
}
